import { useOriginals } from 'hooks';
import useV2Contracts from './useV2Contracts';
import { useQuery } from 'react-query';
import zipObject from 'lodash/zipObject';
import useV1Contracts from './useV1Contracts';
import { BigNumber } from '@ethersproject/bignumber';
import { DROP_CONFIG } from 'components/Drops/dropConfig';
import { DROP, Polling } from 'components/common';
import { utils } from 'ethers';

const useBalances = (
  selectedDrop: DROP | 'all',
  account: string | null,
  refetchInterval: Polling = Polling.fast
) => {
  const { getBalanceOfBatch: v1GetBalanceOfBatch, contractsReady: v1ContractsReady } =
    useV1Contracts();
  const { getBalanceOfBatch: v2GetBalanceOfBatch, contractsReady: v2ContractsReady } =
    useV2Contracts();

  const { sorted } = useOriginals();

  const enabled =
    selectedDrop === DROP.genesis
      ? v1ContractsReady
      : selectedDrop === DROP.enigma
      ? v2ContractsReady
      : v1ContractsReady && v2ContractsReady;

  const { status, data: balances } = useQuery(
    ['balances', account, sorted.length, selectedDrop],
    async () => {
      if (!account) {
        console.log('[fetching balances] account not initialized');
        return {};
      }

      const v1TokenIds: string[] = [];
      const v2TokenIds: string[] = [];

      sorted.forEach(o => {
        if (
          utils.getAddress(o.contractAddress) ===
          utils.getAddress(DROP_CONFIG.genesis.contractAddress)
        ) {
          v1TokenIds.push(o.id);
          v1TokenIds.push(o!.print!.id);
        } else if (
          utils.getAddress(o.contractAddress) ===
          utils.getAddress(DROP_CONFIG.enigma.contractAddress)
        ) {
          v2TokenIds.push(o.id);
          v2TokenIds.push(o!.print!.id);
        }
      });

      if (selectedDrop === DROP.genesis) {
        console.log('[fetching balances] Genesis drop ids', v1TokenIds);

        if (!v1ContractsReady) {
          console.log('[fetching balances] v1 contracts not ready');
          return {};
        }

        const v1Balances: BigNumber[] = await v1GetBalanceOfBatch([account], v1TokenIds);
        const v1Bals = v1Balances.map(b => b.toNumber());
        return zipObject(v1TokenIds, v1Bals);
      } else if (selectedDrop === DROP.enigma) {
        console.log('[fetching balances] Enigma drop ids', v2TokenIds);

        if (!v2ContractsReady) {
          console.log('[fetching balances] v2 contracts not ready');
          return {};
        }

        const v2Balances: BigNumber[] = await v2GetBalanceOfBatch([account], v2TokenIds);
        const v2Bals = v2Balances.map(b => b.toNumber());
        return zipObject(v2TokenIds, v2Bals);
      } else {
        console.log('[fetching balances] Genesis + Enigma ids', [...v1TokenIds, ...v2TokenIds]);

        if (!v1ContractsReady) {
          console.log('[fetching balances] v1 contracts not ready');
          return {};
        }

        if (!v2ContractsReady) {
          console.log('[fetching balances] v2 contracts not ready');
          return {};
        }

        const v1Balances: BigNumber[] = await v1GetBalanceOfBatch([account], v1TokenIds);
        const v2Balances: BigNumber[] = await v2GetBalanceOfBatch([account], v2TokenIds);
        const v1Bals = v1Balances.map(b => b.toNumber());
        const v2Bals = v2Balances.map(b => b.toNumber());
        return zipObject([...v1TokenIds, ...v2TokenIds], [...v1Bals, ...v2Bals]);
      }
    },
    {
      refetchInterval,
      enabled,
    }
  );

  return {
    balances: balances || {},
    status,
  };
};

export default useBalances;
